import { isChrome, getGlobal, isNodeJS } from "../../compat";
import { BufferGeometryUtils } from "../../wgs/scene/BufferGeometry";
import { createWorker } from "./WorkerCreator";
import { DecodeWorkerPool } from "../workers/WorkerPool";
import { initLoadContext } from "../net/endpoints";
import { EventDispatcher } from "../../application/EventDispatcher";
import { getParameterByName } from "../../globals";
import { OtgPriorityQueue, updateGeomImportance } from "../lmvtk/otg/OtgPriorityQueue";
import { LocalDbCache } from "../lmvtk/otg/LocalDbCache";
import { BandwidthOptimizer } from './BandwidthOptimizer';
import { OutOfCoreTileManager } from '../../wgs/scene/out-of-core-tile-manager/OutOfCoreTileManager';
import { USE_OUT_OF_CORE_TILE_MANAGER, USE_UNORM16_GEOMETRY_QUANTIZATION } from '../../wgs/globals';
import { blobToJson } from "../lmvtk/common/StringUtils";
import { MaterialConverter } from "../../wgs/render/MaterialConverter";
import { bin16ToPackedString } from "../lmvtk/otg/HashStrings";
import { BvhNode } from "../../wgs/scene/out-of-core-tile-manager/BvhNode";
/**
 * Fired when new geometry has been received from the server
 *
 * @event OtgResourceCache#MESH_RECEIVE_EVENT
 */
export var MESH_RECEIVE_EVENT = "meshReceived";

/**
 * Fired when fetching a geometry has failed
 *
 * @event OtgResourceCache#MESH_FAILED_EVENT
 */
export var MESH_FAILED_EVENT = "meshFailed";

/**
 * Fired when a new material has been received from the server
 *
 * @event OtgResourceCache#MATERIAL_RECEIVE_EVENT
 */
export var MATERIAL_RECEIVE_EVENT = "materialReceived";

/**
 * Fired when fetching a material has failed
 *
 * @event OtgResourceCache#MATERIAL_FAILED_EVENT
 */
export var MATERIAL_FAILED_EVENT = "materialFailed";

let avp = Autodesk.Viewing.Private;
let useOpfs = getParameterByName('useOPFS') !== 'false' && getGlobal().USE_OPFS !== false && !isNodeJS();
const disableAsFlag = getParameterByName('useAdaptiveStreaming') || getGlobal().USE_ADAPTIVE_STREAMING;
const useAdaptiveStreaming = disableAsFlag !== 'false' && disableAsFlag !== false;
const disableIndexedDb = getParameterByName("disableIndexedDb").toLowerCase() === "true" || getGlobal().DISABLE_INDEXED_DB;
const disableWebSocket = getParameterByName("disableWebSocket").toLowerCase() === "true" || getGlobal().DISABLE_WEBSOCKET;
let disableHashCache = disableIndexedDb || useOpfs || !isChrome();

const GEOM_ERROR = { dummy_value: "error" };
const MAT_ERROR = { dummy_value: "error" };

function initLoadContextGeomCache(msg) {
  var ctx = initLoadContext(msg);
  ctx.disableIndexedDb = disableIndexedDb;
  ctx.disableWebSocket = disableWebSocket;
  return ctx;
}

// Helper function used for cache cleanup
function compareGeomsByImportance(geom1, geom2) {
  return geom1.importance - geom2.importance;
}

/** Shared cache of BufferGeometries and material JSONs used by different OtgLoaders.
 *  @param {Object} [options] - optional parameters
 *  @param {Object} [options.cache] - optional parameters for cache
 *  @param {string} [options.cache.type] - type of cache to use, 'OPFS' or unset
 */
export class OtgResourceCache extends EventDispatcher {
  initialized = true;

  // all geometries, indexed by geom hashes
  _geoms = new Map();
  _mats = new Map();

  _customOpfsCallbacks = new Map();

  _decodeWorkers = [];
  _decoderPorts = [];
  _numLoadersDownloadingFragmentList = 0;

  // track memory consumption
  byteSize = 0;
  refCount = 0;

  // improve hash lookup
  cachedHashesDb = undefined;
  cachedHashes = null;
  cachedHashesPending = false;
  cachedHashesEstimate = undefined;
  cachedHashesEstimatePending = false;
  fromCacheCount = 0;
  fromRemoteCount = 0;

  // track total counts to simplify debugging
  requestsSent = 0;
  requestsReceived = 0;

  _numActiveModels = 0;
  _nextMessageId = 0;


  // A request is called in-progress if we have sent it to the worker and didn't receive a result yet.
  // We restrict the number of requests in progress. If the limit is reached, all additional requests
  // are enqueued.
  _requestsInProgress = 0;

  // When changing this, check constants in OtgLoadWorker
  _timeout = null;

  // As long as something is in the queue, this is set and the recurring task is active.
  _recurringProcessQueuedItemsCallback = null;

  _queue = new OtgPriorityQueue();

  // Keep track of unused geometries.
  // A geometry is tracked as unused if it was used by at least one model and then got removed from all models.
  // This map keep track of which geometries are unused (it stores geom.id -> geometry instance).
  _unusedGeomsMap = new Map();

  // Needed to determine when the dtor can be called (once the last viewer got removed)
  _viewers = [];

  // Keeps track of requests from the OutOfCoreTileManager
  _pendingRequests = [];

  _bwo = undefined;
  _startTs = undefined;

  constructor(options) {
    super();
    this._options = options;

    // A single geometry may be requested by one or more model loaders.
    // This map keeps track of requests already in progress so that
    // we don't issue multiple simultaneously
    this._hash2Requests = new Map();

    let opfsCacheEnabledByOptions = this._options?.cache?.type === 'OPFS';
    if (useOpfs || opfsCacheEnabledByOptions) {
      useOpfs = true;
      disableHashCache = true;
    }

    // Create bound method references for event listeners.
    this._boundHandleWorkerMessage = this._handleWorkerMessage.bind(this);

    // worker for geometry loading
    this._loadWorker = createWorker('OtgLoadWorker');
    this._loadWorker.addEventListener('message', this._boundHandleWorkerMessage);

    // Use the shared DecodeWorkerPool instead of creating private decode workers
    DecodeWorkerPool.register();
    this._decodeWorkers = DecodeWorkerPool.getWorkers();

    // Setup message channels between decode pool workers and the load worker
    for (let i = 0; i < this._decodeWorkers.length; i++) {
      this._decodeWorkers[i].addEventListener('message', this._boundHandleWorkerMessage);
      const channel = new MessageChannel();
      this._decodeWorkers[i].doOperation({
        operation: "INSTALL_INPUT_PORT",
        port: channel.port2,
        useUnorm16GeometryQuantization: USE_UNORM16_GEOMETRY_QUANTIZATION
      }, [channel.port2]);
      this._decoderPorts.push(channel.port1);
    }

    const ctx = initLoadContextGeomCache({
      operation: "INIT_WORKER_OTG",
      ports: this._decoderPorts,
      useOpfs: useOpfs
    });
    const transferList = this._decoderPorts.slice();
    this._loadWorker.doOperation(ctx, transferList);

    // When changing this, check constants in OtgLoadWorker
    this._defaultMaxRequestsInFlight = useAdaptiveStreaming ? BandwidthOptimizer.MIN_FLIGHT : BandwidthOptimizer.FALLBACK_FLIGHT;
    this._maxRequestsInFlight = this._defaultMaxRequestsInFlight;

    // mem limits for cache cleanup
    const MB = 1024 * 1024;
    this._maxMemory = 100 * MB; // geometry limit at which cleanup is activated
    this._minCleanup = 50 * MB; // minimum amount of freed memory for a single cleanup run
  }

  addViewer(viewer) {
    this._viewers.push(viewer);
    this._queue.addViewer(viewer);
  }

  removeViewer(viewer) {
    const index = this._viewers.indexOf(viewer);

    if (index !== -1) {
      this._queue.removeViewer(viewer);
      this._viewers.splice(index, 1);
    }

    if (this._viewers.length === 0) {
      this.dtor();
    }
  }

  dtor() {
    this._viewers = [];

    this._loadWorker.terminate();
    this._loadWorker = null;

    // Close our MessagePorts and remove listeners, then unregister from the shared pool.
    // We don't terminate the pool workers directly -- the pool handles that
    // when the last consumer unregisters.
    for (const port of this._decoderPorts) {
      port.close();
    }
    for (const decodeWorker of this._decodeWorkers) {
      decodeWorker.removeEventListener('message', this._boundHandleWorkerMessage);
    }
    this._decoderPorts = [];
    this._decodeWorkers = [];

    DecodeWorkerPool.unregister();

    // Make sure all unused geoms are cleared
    this.cleanup(true);

    this._geoms = null;
    this._mats = null;

    this._clearHashCache();

    this.initialized = false;

    if (this._timeout) {
      clearTimeout(this._timeout);
      this._timeout = null;
    }
    this.stopProcessing();
  }

  // Chrome's implementation of IndexDb (LevelDb) doesn't perform very well with our workload (heavy scattered writes when loading from the net
  // interspersed with reads). The mere attempt to read a non-existing mesh can become so slow that it starves the download from the backend.
  // Solution is to read the set of hashes in the cache once and use it to skip expensive cache misses.
  //
  // The hash cache will only contain approximate information. If the cache exceeds its quota, LMV will delete some meshes, which is not tracked.
  // This is why only the information "not in cache" is treated as certain.
  _loadHashCache() {
    // db must have been created before
    if (this.cachedHashesPending) {return;}

    this.cachedHashesPending = true;

    this.cachedHashesDb.open(() => {
      this.cachedHashesDb.readAllCachedHashes((hashesArray) => {
        this.cachedHashes = new Set(hashesArray);
        this.cachedHashesPending = false;
      });

      // no need to read anything else
      this.cachedHashesDb = undefined;
    });
  }

  _clearHashCache() {
    this.cachedHashes = null;
  }

  _getHashCacheEstimate() {
    if (this.cachedHashesEstimatePending) {return;}

    this.cachedHashesDb = new LocalDbCache(disableIndexedDb, false);
    this.cachedHashesDb.open(null);
    this.cachedHashesEstimatePending = true;

    this.cachedHashesDb.open(() => {
      this.cachedHashesDb.estimateCachedHashCount((count) => {
        this.cachedHashesEstimate = count;
        // if an error happened (count is undefined), do not try again
        this.cachedHashesEstimatePending = count === undefined;
      });
    });
  }

  _couldBeInCache(hash) {
    return !this.cachedHashes || this.cachedHashes.has(hash);
  }

  // Reading all hashes from the cache can also be expensive. As a remediation, the cache miss rate is monitored. If the estimated
  // number of cache misses is sufficiently high, the number of cache entries is quickly estimated. If the ratio between cache entries
  // and cache misses falls below a threshold, all hashes are read and used for avoiding further cache misses.
  _handleCache() {
    // cache already loaded or irrelevant?
    if (this.cachedHashes || disableHashCache) {return;}

    // constants for the heuristic
    const MIN_TOTAL_COUNT_FOR_FIRST_ESTIMATION = 200;
    const MIN_REMOTE_COUNT_FOR_SECOND_ESTIMATION = 1000;
    const MAX_CACHED_HASHES_PER_REMOTE_REQUEST = 50;

    // sufficient data to make a decision?
    const receivedCount = this.fromCacheCount + this.fromRemoteCount;
    if (receivedCount < MIN_TOTAL_COUNT_FOR_FIRST_ESTIMATION) {return;}

    // estimate how many requests will be served from remote
    const remoteRequestEstimate = this._queue.waitCount() * this.fromRemoteCount / receivedCount;

    // need to find out how many entries are in the cache
    if (this.cachedHashesEstimate == undefined) {
      // only check if there will be sufficiently many network requests
      if (remoteRequestEstimate > MIN_REMOTE_COUNT_FOR_SECOND_ESTIMATION) {this._getHashCacheEstimate();}
      return;
    }

    // check if it is worth loading the cache information given how many network requests we expect
    if (this.cachedHashesEstimate < remoteRequestEstimate * MAX_CACHED_HASHES_PER_REMOTE_REQUEST) {
      this._loadHashCache();
    }
  }

  /**
   * Remove a pending request from _hash2Requests, but makes sure that any attached callbacks are triggered first
   *  @param {string}  hash - hash of the request
   *  @param {boolean|Uint8Array} result - will be passed to the callbacks
   */
  _deletePendingHashRequest(hash, result) {

    // Trigger attached callbacks
    const request = this._hash2Requests.get(hash);
    request?.callbacks.forEach((cb) => cb(result));

    this._hash2Requests.delete(hash);
  }

  _handleWorkerMessage(msg) {
    let initialProcessingDone = false;
    let i = 0;

    let processMessage = (deadline = Infinity) => {
      // It can happen that this task is kept alive in the OutOfCoreTileManager task queue after
      // the viewer has been destroyed. Once a new model is being opened, the task processing will
      // resume and an old task might get processed. The destructor for the geom cache will set _geoms
      // to null and we check here whether this is a task from a destroyed geomcache. In that case,
      // we just report true to indicate that the task can be cleared.
      if (this._geoms === null) {
        return true;
      }

      if (!initialProcessingDone) {
        if (msg.data.event && msg.data.properties) {
          avp.analytics.track(msg.data.event, msg.data.properties);
          return true;
        }

        if (typeof msg.data.customOpfsMessageId === "number") {
          this._customOpfsCallbacks.get(msg.data.customOpfsMessageId)(msg.data.result);
          this._customOpfsCallbacks.delete(msg.data.customOpfsMessageId);
          this.flushCacheAndDisconnect();
          return true;
        }

        //Schedule another spin through the task queue
        if (!USE_OUT_OF_CORE_TILE_MANAGER && !this._queue.isEmpty() && !this._timeout) {
          this._timeout = setTimeout(() => this.processQueuedItems(), 0);
        }

        if (msg.data.error) {
          var error = msg.data.error;
          var hash = error.hash;

          // inform affected clients.
          if (error.resourceType === "m") {
            this._mats.set(hash, MAT_ERROR);
            this.fireEvent({ type: MATERIAL_FAILED_EVENT, hash: hash });
            console.error("Error loading material.", hash, error.msg, error.args);
          } else {
            this._geoms.set(hash, GEOM_ERROR);
            this.fireEvent({ type: MESH_FAILED_EVENT, hash: hash });
            console.error("Error loading mesh.", hash, error.msg, error.args);
          }

          this._deletePendingHashRequest(hash, false);

          // track number of requests in progress
          this._requestsInProgress--;
          this.requestsReceived++;
          return true;
        }
        initialProcessingDone = true;
      }

      let totalSize = 0;

      if (msg.data.blobs) {
        // Just for the prism case, we might have to go async. Note that the out of core tile manager
        // doesn't know about this and the time budgeting will be less accurate.
        // eslint-disable-next-line no-floating-promise/no-floating-promise
        (async () => {
          for (let i = 0; i < msg.data.blobs.length; i++) {
            const hash = msg.data.hashes[i];
            const blob = msg.data.blobs[i];

            this._requestsInProgress--;
            this.requestsReceived++;

            if (msg.data.resourceType === "m") {

              const mat = blobToJson(blob);

              if (MaterialConverter.isPrismMaterial(mat)) {
                await MaterialConverter.loadMaterialConverterPrismLibrary();
              }

              // add material to cache
              this._mats.set(hash, mat);

              // pass material to all receiver callbacks
              this.fireEvent({ type: MATERIAL_RECEIVE_EVENT, material: mat, hash: hash });
            }

            if (blob) {
              totalSize += blob.byteLength;
            }
            this._deletePendingHashRequest(hash, blob);
          }
        })();
      } else if (msg.data.mdatas) {

        const mdatas = msg.data.mdatas;

        for (i; i < mdatas.length; i++) {
          this._requestsInProgress--;
          this.requestsReceived++;

          var mdata = mdatas[i];

          if (mdata.hash && mdata.mesh) {

            // convert geometry data to GeometryBuffer
            // Moving this to the OtgLoadWorker results in "WebGL: INVALID_OPERATION: drawElements: no buffer is bound to enabled attribute"
            var geom = BufferGeometryUtils.meshToGeometry(mdata);

            // add geom to cache
            this.addGeometry(mdata.hash, geom);

            // free old unused geoms if necessary
            this.cleanup();

            // pass geometry to all receiver callbacks
            this.fireEvent({ type: MESH_RECEIVE_EVENT, geom: geom });

            this._deletePendingHashRequest(mdata.hash, true);

            mdata.fromCache ? ++this.fromCacheCount : ++this.fromRemoteCount;
            totalSize += geom.byteSize;
          }

          if (i % 20 === 0 && performance.now() >= deadline) {
            i++;
            return false;
          }
        }
      }

      this.bandwidthOptimizer()?.onResourceReceived(
        msg.data.blobs?.length || msg.data.mdatas?.length, totalSize);

      this._handleCache();

      return true;
    };

    // We compute the priorities in a separate task since unpacking the messages from the work already
    // takes considerable time and has to be performed to determine the priorities. This function will then
    // create individual tasks
    const createMessageProcessingTasks = () => {
      // Determine priority for this task as the maximum of the priority of all requests answers contained in the message
      let priority = -Infinity;
      if (msg.data.blobs) {
        for (let i = 0; i < msg.data.blobs.length; i++) {
          const hash = msg.data.hashes[i];
          if (priority < this._hash2Requests.get(hash)?.priority)
          priority = this._hash2Requests.get(hash).priority;
        }
      } else if (msg.data.mdatas) {
        const mdatas = msg.data.mdatas;
        for (let i = 0; i < mdatas.length; i++) {
          if (mdatas[i].hash && mdatas[i].mesh) {
            priority = Math.max(this._hash2Requests.get(mdatas[i].hash)?.priority ?? -Infinity, priority);
          }
        }
      }

      // if no priority was specified, assume max priority
      if (priority === -Infinity)
      priority = Infinity;

      OutOfCoreTileManager.addOneShotTask(processMessage, priority);

      return true;
    };

    if (USE_OUT_OF_CORE_TILE_MANAGER) {
      OutOfCoreTileManager.addOneShotTask(createMessageProcessingTasks, Infinity);
    } else {
      processMessage();
    }
  }

  bandwidthOptimizer() {
    if (!this._bwo && !this._startTs) {
      this._startTs = Date.now();
    }

    // start the adaptive streaming after establishing initial conditions
    if (this._bwo || this.requestsReceived < this._maxRequestsInFlight * 2) {
      return this._bwo;
    }

    // create initial fragment average
    // TODO: Need to figure out if we need separate out materials and geometries...
    const initialAssetEstimate = this.byteSize / this.requestsReceived * 0.9;
    const avgBytesSec = this.byteSize / ((Date.now() - this._startTs) / 1000);

    if (useAdaptiveStreaming) {
      this._bwo = BandwidthOptimizer.createAndStart(
        this._requestsInProgress, initialAssetEstimate, avgBytesSec,
        (flightSize) => this._maxRequestsInFlight = flightSize,
        (count) => {
          const remaining = this._queue.waitingTasks.length / (this.requestsSent + this._queue.waitingTasks.length) * 100;
          if (remaining > 5) {// skip updating the websocket count if the amount of tasks in the queue is low compared to total count (5%)
            this._loadWorker.doOperation({ operation: "SET_OTGWS_COUNT", count });
          }
        }
      );
    }

    return this._bwo;
  }

  modelAdded(modelUrn, lineageUrn) {
    var msg = {
      operation: "ADD_MODEL_OTG",
      modelUrn: modelUrn,
      cacheBucket: lineageUrn
    };

    this._loadWorker.doOperation(initLoadContextGeomCache(msg));

    // a new model gets added => restart cache stats
    this.fromCacheCount = 0;
    this.fromRemoteCount = 0;

    this._numActiveModels++;
  }

  modelRemoved() {
    this._numActiveModels--;
    this.flushCacheAndDisconnect();
  }

  flushCacheAndDisconnect() {
    // don't disconnect if there are any active models or custom reads/stores left
    if (this._numActiveModels !== 0 || this._customOpfsCallbacks.size > 0) {
      return;
    }

    // let go of accumulated hashes.
    this._clearHashCache();

    var msg = {
      operation: "FLUSH_CACHE_AND_DISCONNECT_OTG"
    };
    this._loadWorker.doOperation(msg);
  }

  /**  Get a geometry from cache or load it.
   *    @param {string}   url         - full request url of the geometry/ies resource
   *    @param {boolean}  isCDN       - deprecated, unused
   *    @param {string}   geomHash    - hash key to identify requested geometry/ies
   *    @param {int} geomIdx          - deprecated, unused
   *    @param {string}   queryParams - additional param passed to file query
   */
  requestGeometry(url, isCDN, geomHash, geomIdx, queryParams, lineageUrn, addToQueue = true) {

    // if this geometry is in memory, just return it directly
    var geom = this._geoms.get(geomHash);
    if (geom === GEOM_ERROR) {
      //it failed to load previously
      if (isNodeJS()) {
        setTimeout(() => this.fireEvent({ type: MESH_FAILED_EVENT, hash: geomHash }), 0);
      } else {
        this.fireEvent({ type: MESH_FAILED_EVENT, hash: geomHash });
      }
      return;
    } else if (geom) {
      //it was already cached
      if (isNodeJS()) {
        setTimeout(() => this.fireEvent({ type: MESH_RECEIVE_EVENT, geom: geom }), 0);
      } else {
        this.fireEvent({ type: MESH_RECEIVE_EVENT, geom: geom });
      }
      return;
    }

    // if geometry is already loading, just increment
    // the request counter.
    var task = this._hash2Requests.get(geomHash);
    if (task && task.refcount) {
      task.importanceNeedsUpdate = true;
      task.refcount++;
      return;
    }

    // geom is neither in memory nor loading.
    // we have to request it.
    var msg = {
      operation: "LOAD_CDN_RESOURCE_OTG",
      type: "g",
      url: url,
      cacheBucket: lineageUrn,
      hash: geomHash,
      queryParams: queryParams,
      importance: 0.0,
      importanceNeedsUpdate: true, // compute actual importance later in updatePriorities
      refcount: 1,
      requested: false,
      callbacks: new Array()
    };

    if (addToQueue) {
      this._queue.addTask(msg);
    }
    this._hash2Requests.set(geomHash, msg);

    if (USE_OUT_OF_CORE_TILE_MANAGER) {
      if (addToQueue) {
        this.startProcessing();
      }
    } else if (!this._timeout) {
      this._timeout = setTimeout(() => this.processQueuedItems(), 0);
    }
  }

  requestMaterial(url, isCDN, matHash, matIdx, queryParams, lineageUrn) {

    // if this material is in memory, just return it directly
    var mat = this._mats.get(matHash);
    if (mat === MAT_ERROR) {
      //it failed to load previously
      setTimeout(() => this.fireEvent({ type: MATERIAL_FAILED_EVENT, error: mat, hash: matHash, repeated: true }), 0);
      return;
    } else if (mat) {
      //it was already cached
      // TODO fire the event synchronously instead of using setTimeout.
      // This would be faster and simplify some things (see setTimeout comment further down in this file),
      // but is not straightforward as tryToActivateFragment would become reentrant and the material processing logic becomes confused.
      // (It is already if requestGeometry fires its received event, but we got lucky there and it just works)
      setTimeout(() => this.fireEvent({ type: MATERIAL_RECEIVE_EVENT, material: mat, hash: matHash }));
      return;
    }

    // if material is already loading, just increment
    // the request counter.
    var task = this._hash2Requests.get(matHash);
    if (task && task.refcount) {
      task.refcount++;
      return;
    }

    // material is neither in memory nor loading.
    // we have to request it.
    var msg = {
      operation: "LOAD_CDN_RESOURCE_OTG",
      type: "m",
      urls: [url],
      cacheBuckets: [lineageUrn],
      hashes: [matHash],
      queryParams: queryParams,
      refcount: 1,
      couldBeInCache: [this._couldBeInCache(matHash)],
      requested: true,
      callbacks: new Array()
    };

    this._hash2Requests.set(matHash, msg);

    //Material requests are sent to the worker immediately, without going through the
    //priority queue.
    this._loadWorker.doOperation(initLoadContextGeomCache(msg));
    this._requestsInProgress++;
    this.requestsSent++;
  }

  startProcessing() {
    if (!this._recurringProcessQueuedItemsCallback) {
      this._recurringProcessQueuedItemsCallback = () => {return this.processQueuedItems();};
      OutOfCoreTileManager.addRecurringTask(this._recurringProcessQueuedItemsCallback);
    }
  }

  stopProcessing() {
    if (this._recurringProcessQueuedItemsCallback) {
      OutOfCoreTileManager.removeRecurringTask(this._recurringProcessQueuedItemsCallback);
      this._recurringProcessQueuedItemsCallback = null;
    }
  }

  /**
   * Requests a batch of geometries and/or materials by their hashes.
   * @param {Set<string>} hashes - Set of resource hashes to request.
   * @param {number} priority - Priority with which to process the response.
   * @returns {Promise<boolean>} Promise resolving to true if all loaded successfully, false otherwise.
   */
  batchRequest(hashes, priority) {
    let numMissingHashes = hashes.size;
    let loadCompleted;
    let promise = new Promise((resolve) => {
      loadCompleted = resolve;
    });
    let allLoadedSuccessfully = true;
    const callback = (success) => {
      --numMissingHashes;
      if (!success) {
        // A required geom or material failed to load.
        allLoadedSuccessfully = false;
      }
      if (numMissingHashes === 0) {
        loadCompleted(allLoadedSuccessfully);
      }
    };

    for (const hash of hashes) {
      let request = this._hash2Requests.get(hash);

      // requestMaterial uses setTimeout to fire the received event if the material is already there.
      // So it can happen that materials get requested by the loader that are actually already known to the OtgResourceCache.
      // So we need to catch that here.
      if (this._mats.has(hash)) {
        // Because of the setTimeout, we must delay the callback.
        // Otherwise, if we fire the callback here immediately, consolidation will be attempted before the loader received the material.
        setTimeout(() => callback(true), 0);
        continue;
      }
      request.callbacks.push(callback);

      if (request.requested) {
        continue;
      }
      request.requested = true;
      request.priority = Math.max(priority, request.priority ?? 0);
      this._pendingRequests.push(request);
    }

    // If we accumulated enough work (completely arbitrary number),
    // or no more work will come this tick (because the max number of in flight requests has been reached),
    // send it immediately to keep the worker busy.
    if (this._pendingRequests.length > 1000 || this._requestsInProgress + this._pendingRequests.length >= this._getMaxRequestsInFlight()) {
      this.sendRequests(this._pendingRequests);
      this._pendingRequests = [];
    } else if (!this._timeout && this._pendingRequests.length > 0) {
      // Otherwise, delay sending the requests to batch with other nodes.
      // In particular when loading small nodes from cache, we don't want to send 100s of messages per tick.
      // This will be outside of the OOCTM time budget, but should be fast for < 1000 requests.
      this._timeout = setTimeout(() => {
        this._timeout = null;
        this.sendRequests(this._pendingRequests);
        this._pendingRequests = [];
      }, 0);
    }

    return promise;
  }

  /**
   * Returns true if there is bandwidth budget available for new download requests.
   * Used by OutOfCoreTileManager to globally limit download-related tasks.
   * @returns {boolean}
   */
  hasBandwidthBudget() {
    return this._requestsInProgress < this._getMaxRequestsInFlight();
  }

  processQueuedItems() {

    // When we load from cache, this is not relevant, since the queue is not filled at all.
    // However, if we started out loading from queue, and then cut over to the OOCTM when the BVH got ready,
    // we want to skip processing the queue as long as the OOCTM still has download tasks.
    // The OOCTM does prioritization for much less CPU time than updateRequestPriorities.
    // Ideally we'd clear the queue entirely as soon as the OOCTM sends work, but if a very small
    // model finished, a large model might still not have a BVH and want to process the queue.
    if (OutOfCoreTileManager.hadDownloadTasksLastTick()) {
      return;
    }

    let maxRequestsInFlight = this._getMaxRequestsInFlight();

    var howManyCanWeDo = maxRequestsInFlight - this._requestsInProgress;

    // avoid the overhead of very small messages
    if (howManyCanWeDo <= maxRequestsInFlight * 0.01) {
      if (!USE_OUT_OF_CORE_TILE_MANAGER) {
        this._timeout = setTimeout(() => this.processQueuedItems(), 30);
      }
      return;
    }

    // recompute importance for each geometry and sort queue by decreasing priority
    this._queue.updateRequestPriorities();

    let requests = [];

    // process OtgPriorityQueue
    while (!this._queue.isEmpty() && requests.length < howManyCanWeDo) {
      var task = this._queue.takeTask();
      if (task.requested) {
        continue;
      }
      // can happen when cutting over to loading from BVH. Then we still need to process the queue to clear it.
      if (this._geoms.has(task.hash)) {
        continue;
      }
      requests.push(task);
      task.requested = true;
    }

    this.sendRequests(requests);

    if (USE_OUT_OF_CORE_TILE_MANAGER) {
      if (this._queue.isEmpty()) {
        this.stopProcessing();
      }
    } else {
      if (this._queue.isEmpty()) {
        this._timeout = undefined;
      } else {
        this._timeout = setTimeout(() => this.processQueuedItems(), 30);
      }
    }
  }

  sendRequests(requests) {
    if (requests.length === 0) {
      return;
    }
    const msg = {
      operation: "LOAD_CDN_RESOURCE_OTG",
      type: "g",
      urls: requests.map((r) => r.url),
      cacheBuckets: requests.map((r) => r.cacheBucket),
      hashes: requests.map((r) => r.hash),
      queryParams: requests[0].queryParams,
      couldBeInCache: requests.map((r) => this._couldBeInCache(r.hash))
    };

    // send request to worker
    this._loadWorker.doOperation(initLoadContextGeomCache(msg));
    this._requestsInProgress += requests.length;
    this.requestsSent += requests.length;
  }

  // remove all open requests of this client
  // input is a map whose keys are geometry hashes
  cancelRequests(hashes) {

    for (var hash of hashes) {
      var task = this._hash2Requests.get(hash);

      if (task)
      task.refcount--;
      /*
      if (task.refcount === 1) {
          _hash2Requests.delete(hash);
      }*/
    }

    this._queue.filterTasks((hash) => {
      // TODO: Analyze why `req` can be undefined. Story: https://autodesk.atlassian.net/browse/FLUENT-5734
      const req = this._hash2Requests.get(hash);
      const keep = req && req.refcount;
      if (!keep) {
        this._hash2Requests.delete(hash);
      }
      return keep;
    });

    // TODO: To make switches faster, we should also inform the worker thread,
    //       so that it doesn't spend too much time with loading geometries that noone is waiting for.
  }

  // To prioritize a geometry, we track the bbox surface area of all fragments using it.
  //
  // For this, this function must be called for each new loaded fragment.
  //  @param {RenderModel} model
  //  @param {number}      fragId
  updateGeomImportance(model, fragId) {
    return updateGeomImportance(model, fragId);
  }

  cleanup(force) {

    if (this._unusedGeomsMap.size === 0 || !force && this.byteSize < this._maxMemory) {
      return;
    }

    var _unusedGeoms = [];
    for (let geom of this._unusedGeomsMap.values()) {
      _unusedGeoms.push(geom);
    }

    // Sort unused geoms by ascending importance
    _unusedGeoms.sort(compareGeomsByImportance);

    // Since cleanup is too expensive to run per geometry,
    // we always remove a bit more than strictly necessary,
    // so that we can load some more new geometries before we have to
    // run cleanup again.
    var targetMem = force ? 0 : this._maxMemory - this._minCleanup;

    // Remove geoms until we reach mem target
    var i = 0;
    for (; i < _unusedGeoms.length && this.byteSize >= targetMem; i++) {

      var geom = _unusedGeoms[i];

      // remove it from cache
      this._geoms.delete(geom.hash);
      this._unusedGeomsMap.delete(geom.id);

      // update mem consumption. Note that we run this only for geoms that
      // are not referenced by any RenderModel in memory, so that removing them
      // should actually free memory.
      this.byteSize -= geom.byteSize;

      // Dispose GPU mem.
      // NOTE: In case we get performance issues in Chrome, try commenting this out
      // (see hack in GeometryList.dispose)
      geom.dispose();
    }
  }

  // Wait for specific hashes and push their priority to finish faster.
  //
  // Note: This function does not trigger own requests, i.e. can only be used for hashes of models
  //       that are currently loading.
  //
  //  @param {Object} hashMap          - keys specify hashes. All keys with hashMap[key]===(true|number) will be loaded. If a number is given, it will be used as priority.
  //  @param {function(Object)} onDone - called with hashMap. hashMap[hash] will contain the geometry.
  waitForGeometry(hashMap, onDone) {

    // track how many of our geoms are finished
    var geomsDone = 0;
    var geomsTodo = this._queue.makeUrgent(hashMap);

    // avoid hanging if hashMap is empty
    if (geomsTodo === 0) {
      if (hashMap) {
        onDone(hashMap);
        return;
      }
    }

    this.processQueuedItems();

    const onGeomDone = (hash, geom) => {
      // If a geometry is not loading anymore, its priority has no relevance anymore.
      // Note that this is generally true - even if we didn't set the priority in this waitForGeometry call.
      this._queue.removeUrgent(hash);

      // Only care for geometries that we need to fill the hashMap values
      if (!hashMap[hash] === true && typeof hashMap[hash] !== 'number') {
        return;
      }

      hashMap[hash] = geom;

      // check if all done
      geomsDone++;
      if (geomsDone < geomsTodo) {
        return;
      }

      // cleanup listeners
      this.removeEventListener(MESH_RECEIVE_EVENT, onGeomReceived);
      this.removeEventListener(MESH_FAILED_EVENT, onGeomFailed);

      onDone(hashMap);
    };

    const onGeomReceived = (event) => {onGeomDone(event.geom.hash, event.geom);};
    const onGeomFailed = (event) => {onGeomDone(event.hash, undefined);};

    this.addEventListener(MESH_RECEIVE_EVENT, onGeomReceived);
    this.addEventListener(MESH_FAILED_EVENT, onGeomFailed);

    // Don't wait forever for any meshes that were already loaded
    for (let hash in hashMap) {
      var geom = this._geoms.get(hash);
      if (geom) {
        onGeomDone(hash, geom);
      }
    }
  }

  getGeometry(hash) {
    const geom = this._geoms.get(hash);
    if (geom === GEOM_ERROR) {
      return null;
    }
    return geom;
  }

  addGeometry(hash, geom) {
    this._geoms.set(hash, geom);

    // track summed cache size in bytes
    this.byteSize += geom.byteSize;

    if (Object.prototype.hasOwnProperty.call(geom, '_modelRefCount')) {
      return;
    }
    geom._modelRefCount = 0;

    const prototype = Object.getPrototypeOf(geom);
    if (Object.prototype.hasOwnProperty.call(prototype, 'modelRefCount')) {
      return;
    }
    Object.defineProperty(prototype, 'modelRefCount', {
      get: geometry_modelRefCount_get
    });
    prototype.setModelRefCount = geometry_setModelRefCount;
  }

  setGeomRefCount(geom, value) {
    if (geom._modelRefCount === 0 && value >= 1) {
      const unused = this._unusedGeomsMap.get(geom.id);
      if (unused) {
        this._unusedGeomsMap.delete(geom.id);
      }
    } else if (value === 0) {
      this._unusedGeomsMap.set(geom.id, geom);
    }
    geom._modelRefCount = value;
  }

  // Add material to cache. Note that the cache doesn't store actual Material instances,
  // but rather the source data from the materials file.
  //  @param {string}     hash
  //  @param {Uint8Array} data - a Uint8 blob, containing a material-file json as Utf8.
  addMaterialData(hash, data) {
    this._mats.set(hash, blobToJson(data));
  }

  clearOpfsCache() {
    // Note I wanted to print navigator.storage.estimate before and after, but it was incorrect.
    // Chrome underreported file system usage, it looked like it was confused by a second directory in the filesystem
    const msg = {
      operation: "CLEAR_OPFS_CACHE"
    };
    this._loadWorker.doOperation(msg);
  }

  // For error diagnosis: If something gets stuck during loading, this report helps
  // figuring out where it happens.
  reportLoadingState() {

    // Report main thread stats
    console.log('OtgResourceCache:', {
      sent: this.requestsSent,
      received: this.requestsReceived
    });

    const msg = {
      operation: "REPORT_LOADING_STATE"
    };
    this._loadWorker.doOperation(msg);
  }

  /**
   * Stores data in the OPFS cache. Can fail e.g. if the cache is not open or on quota exceeded
   * @param {String} bucketName
   * @param {String[]} identifiers
   * @param {Uint8Array[]} datas
   * @returns promise that resolves to a boolean indicating whether the store was successful
   */
  opfsStore(bucketName, identifiers, datas) {
    return new Promise((resolve) => {
      const messageId = this._nextMessageId++;
      this._customOpfsCallbacks.set(messageId, resolve);
      const msg = {
        operation: "OPFS_CACHE_STORE",
        customOpfsMessageId: messageId,
        bucketName,
        identifiers,
        datas
      };
      this._loadWorker.doOperation(msg, datas.map((data) => data.buffer));
    });
  }

  /**
   * Informs the OTGResourceCache that a fragment list download has started and that
   * the download of geometries should be throttled while the download is ongoing.
   */
  throttleDownload() {
    this._numLoadersDownloadingFragmentList++;
  }

  /**
   * Stops throttling the download of geometries after the fragment list download has completed.
   */
  stopThrottleDownload() {
    this._numLoadersDownloadingFragmentList--;
  }

  _getMaxRequestsInFlight() {
    if (USE_OUT_OF_CORE_TILE_MANAGER && this._numLoadersDownloadingFragmentList > 0) {
      return Math.min(this._maxRequestsInFlight, BandwidthOptimizer.THROTTLED_FLIGHT);
    }
    return this._maxRequestsInFlight;
  }

  /**
   * Retrieves data from the OPFS cache
   * @param {String} cacheBucket
   * @param {String} hash
   * @param {Number|BvhNode} [priorityOrNode] used to determine the priority for processing the results in the  OutOfCoreTileManager
   * @returns {Uint8Array}
   */
  getResource(cacheBucket, hash, priorityOrNode = Infinity) {
    if (!useOpfs) {
      return null;
    }
    console.assert(hash.byteLength === 20);
    hash = bin16ToPackedString(new Uint16Array(hash));

    var msg = {
      operation: "LOAD_CDN_RESOURCE_OTG",
      type: "f",
      cacheBuckets: [cacheBucket],
      hashes: [hash],
      callbacks: new Array(),
      get priority() {
        return priorityOrNode instanceof BvhNode ? priorityOrNode.getCurrentScreenSpaceError(priorityOrNode.getNextStage()) : priorityOrNode;
      }
    };
    this._hash2Requests.set(hash, msg);

    this._loadWorker.doOperation(initLoadContextGeomCache(msg));
    this._requestsInProgress++;
    this.requestsSent++;

    return new Promise((resolve) => {
      msg.callbacks.push((data) => resolve(data));
    });
  }

}


// These two functions are intentionally defined outside of the
// upper scope to make sure they don't hold a reference to the
// closure. They will be added to a prototype later and thus
// would prevent the closure from being garbage collected.
function geometry_modelRefCount_get() {
  return this._modelRefCount;
}

let warningAboutRefcountPrinted = false;
function geometry_setModelRefCount(value, geomCache) {
  if (!geomCache) {
    // This should never happen, but as a fallback we will set the refcounts to 1
    // if we don't have a geomCache to keep track of them.
    if (!warningAboutRefcountPrinted) {
      console.warn("geometry_setModelRefCount: geomCache is not defined");
      warningAboutRefcountPrinted = true;
    }
    this._modelRefCount = 1;
  } else {
    geomCache.setGeomRefCount(this, value);
  }
}